// Decorative SVG/CSS art for each theme. Everything here is aria-hidden and
// animates only transform/opacity so it stays smooth on older phones.
import { useId } from 'react';

// Stable pseudo-random so particles don't jump between renders.
function rand(seed) {
    let s = seed;
    return () => {
        s = (s * 9301 + 49297) % 233280;
        return s / 233280;
    };
}

export function Particles({ count = 14, className, seed = 7 }) {
    const r = rand(seed);
    return (
        <div className={`fx-particles ${className}`}>
            {Array.from({ length: count }, (_, i) => (
                <span
                    key={i}
                    style={{
                        '--x': `${(r() * 100).toFixed(1)}%`,
                        '--d': `${(-r() * 12).toFixed(2)}s`,
                        '--t': `${(7 + r() * 8).toFixed(2)}s`,
                        '--s': `${(2 + r() * 4).toFixed(1)}px`,
                        '--w': `${((r() - 0.5) * 60).toFixed(0)}px`,
                    }}
                />
            ))}
        </div>
    );
}

/* ============ HexLink (default) ============ */
export function HexBackdrop() {
    const id = useId().replace(/:/g, '');
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <div className="fx-glow fx-glow-a" />
            <div className="fx-glow fx-glow-b" />
            <svg className="fx-hexgrid" width="100%" height="100%">
                <defs>
                    <pattern id={`hex-${id}`} width="28" height="48.5" patternUnits="userSpaceOnUse" patternTransform="scale(1.3)">
                        <path d="M14 0 28 8.08v16.17L14 32.33 0 24.25V8.08Z M14 32.33V48.5 M0 8.08 -14 0 M28 8.08 42 0" fill="none" stroke="currentColor" strokeWidth="0.6" />
                    </pattern>
                    <radialGradient id={`hexfade-${id}`} cx="50%" cy="18%" r="75%">
                        <stop offset="0" stopColor="#fff" stopOpacity="1" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                    </radialGradient>
                    <mask id={`hexmask-${id}`}>
                        <rect width="100%" height="100%" fill={`url(#hexfade-${id})`} />
                    </mask>
                </defs>
                <rect width="100%" height="100%" fill={`url(#hex-${id})`} mask={`url(#hexmask-${id})`} />
            </svg>
            <div className="fx-scan" />
        </div>
    );
}

export function HexPhotoDecor() {
    return (
        <svg className="fx-hexring" viewBox="0 0 100 100" aria-hidden="true">
            <path d="M50 2 91.6 26v48L50 98 8.4 74V26Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeDasharray="6 5" />
        </svg>
    );
}

/* ============ Skully ============ */
function fitSize(text, max, base) {
    const len = Math.max(text.length, 1);
    return Math.min(base, Math.floor((max / len) * 1.6));
}

// Straight rectangular patch (name-tape style) with a stitched border.
export function Patch({ text, position = 'top' }) {
    const label = (text || '').toUpperCase();
    if (!label) return null;
    return (
        <div className={`fx-patch fx-patch-${position}`}>
            <span style={{ fontSize: fitSize(label, 250, position === 'top' ? 22 : 17) }}>{label}</span>
        </div>
    );
}

export function SkullyBackdrop() {
    const id = useId().replace(/:/g, '');
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <svg className="fx-leather" width="100%" height="100%">
                <filter id={`grain-${id}`}>
                    <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" stitchTiles="stitch" />
                    <feColorMatrix values="0 0 0 0 0.5  0 0 0 0 0.42  0 0 0 0 0.3  0 0 0 0.55 0" />
                </filter>
                <rect width="100%" height="100%" filter={`url(#grain-${id})`} />
            </svg>
            <div className="fx-skully-glow" />
            <Particles count={18} className="fx-embers" seed={45} />
            <div className="fx-vignette" />
        </div>
    );
}

export function SkullyPhotoDecor() {
    return (
        <svg className="fx-stitch-ring" viewBox="0 0 100 100" aria-hidden="true">
            <circle cx="50" cy="50" r="48.5" className="fx-ring-gold" />
            <circle cx="50" cy="50" r="45.2" className="fx-ring-stitch" />
        </svg>
    );
}

/* ============ Freedom ============ */
// Clean red, white and blue: a navy header band edged with red and white stripes. No flag, no motion.
export function FreedomBackdrop() {
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <div className="fx-rwb-band" />
            <div className="fx-paper" />
        </div>
    );
}

export function FreedomPhotoDecor() {
    return <span className="fx-freedom-ring" aria-hidden="true" />;
}

/* ============ Cockatoo ============ */
export function CockatooBackdrop() {
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <div className="fx-blob fx-blob-a" />
            <div className="fx-blob fx-blob-b" />
            <div className="fx-blob fx-blob-c" />
            <div className="fx-confetti">
                <Particles count={12} className="fx-feathers" seed={88} />
            </div>
        </div>
    );
}

export function CockatooCrest() {
    const id = useId().replace(/:/g, '');
    const feathers = [-38, -19, 0, 19, 38];
    return (
        <svg className="fx-crest" viewBox="0 0 120 70" aria-hidden="true">
            <defs>
                <linearGradient id={`crest-${id}`} x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0" stopColor="#ff4fa3" />
                    <stop offset="0.6" stopColor="#ff9f1c" />
                    <stop offset="1" stopColor="#ffd84d" />
                </linearGradient>
            </defs>
            {feathers.map((a, i) => (
                <path
                    key={a}
                    className="fx-feather"
                    style={{ '--a': `${a}deg`, '--i': i }}
                    d="M60 70 C50 48 52 22 60 4 C68 22 70 48 60 70Z"
                    fill={`url(#crest-${id})`}
                />
            ))}
        </svg>
    );
}

export function CockatooPhotoDecor() {
    return <span className="fx-conic-ring" aria-hidden="true" />;
}

/* ============ Neon Pink ============ */
export function NeonBackdrop() {
    const id = useId().replace(/:/g, '');
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <svg className="fx-bricks" width="100%" height="100%">
                <defs>
                    <pattern id={`brick-${id}`} width="64" height="32" patternUnits="userSpaceOnUse">
                        <path d="M0 0.5H64 M0 16.5H64 M0.5 0V16 M32.5 16V32" stroke="currentColor" strokeWidth="1" fill="none" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill={`url(#brick-${id})`} />
            </svg>
            <div className="fx-neon-haze" />
            <div className="fx-neon-haze fx-neon-haze-b" />
            <div className="fx-scanlines" />
        </div>
    );
}

export function NeonPhotoDecor() {
    return <span className="fx-neon-ring" aria-hidden="true" />;
}

/* ============ Grey Demon ============ */
// Magnetic grey over carbon fiber, like a Hellcat / GT350: twin stripes, clear-coat sheen, machined ring.
export function DemonBackdrop() {
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <div className="fx-carbon" />
            <div className="fx-twin-stripes" />
            <div className="fx-clearcoat" />
            <div className="fx-vignette" />
        </div>
    );
}

export function DemonPhotoDecor() {
    return <span className="fx-bezel" aria-hidden="true" />;
}

/* ============ Venom Drip ============ */
export function VenomBackdrop() {
    return (
        <div className="hx-backdrop" aria-hidden="true">
            <div className="fx-toxic" />
            <div className="fx-toxic fx-toxic-b" />
        </div>
    );
}

export function VenomPhotoDecor() {
    return <span className="fx-venom-ring" aria-hidden="true" />;
}
